import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import { API_URLS } from '../../config/api';
import { formatDate } from '../../utils/dateUtils';
import { 
  CalendarIcon, 
  ClockIcon, 
  UserGroupIcon, 
  MapPinIcon,
  ArrowRightIcon,
  ArrowUturnLeftIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const AvailableRides = () => {
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [cities, setCities] = useState([]);
  const [bookingRide, setBookingRide] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState(1);
  const [specialRequests, setSpecialRequests] = useState('');
  const [bookingStatus, setBookingStatus] = useState({ loading: false, error: '', success: false });

  // Helper function to extract city and postcode from address
  const getCityAndPostcode = (address) => {
    if (!address) return 'N/A';
    
    // Split address by commas
    const parts = address.split(',').map(part => part.trim());
    
    if (parts.length >= 2) {
      // Get the last part (likely contains city and postcode)
      const lastPart = parts[parts.length - 1];
      // If it's just a postcode pattern, get city from second last part
      const postcodePattern = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i;
      
      if (postcodePattern.test(lastPart.trim()) && parts.length >= 3) {
        // Format: "Street, City, Postcode"
        return `${parts[parts.length - 2]}, ${lastPart}`;
      } else {
        // Assume last part contains "City Postcode"
        return lastPart;
      }
    }
    
    return address; // Fallback to full address
  };

  // Helper function to extract only city name (for filtering)
  const extractCityName = (address) => {
    if (!address) return null;
    
    console.log('Processing address:', address);
    
    // Split address by commas and clean whitespace
    const parts = address.split(',').map(part => part.trim());
    console.log('Address parts:', parts);
    
    // Canadian postcode pattern: A1A 1A1 or A1A1A1
    const postcodePattern = /[A-Z]\d[A-Z]\s?\d[A-Z]\d/i;
    
    if (parts.length >= 3) {
      // Format: "Street, City, Province/Postcode" or "Street, City, Postcode"
      const lastPart = parts[parts.length - 1]; // Could be "ON L5T 1J6" or just "L5T 1J6"
      const secondLastPart = parts[parts.length - 2]; // Should be city
      
      console.log('3+ parts - Second last:', secondLastPart, 'Last:', lastPart);
      
      // If last part contains a postcode, return second last part as city
      if (postcodePattern.test(lastPart)) {
        console.log('Found postcode in last part, returning city:', secondLastPart);
        return secondLastPart.trim();
      }
    }
    
    if (parts.length === 2) {
      // Format: "Street, City Postcode" or "Street, City"
      const lastPart = parts[parts.length - 1];
      console.log('2 parts - Last part:', lastPart);
      
      // Check if last part contains a postcode
      if (postcodePattern.test(lastPart)) {
        // Remove the postcode from the end
        const cityWithoutPostcode = lastPart.replace(postcodePattern, '').trim();
        console.log('Removed postcode, city is:', cityWithoutPostcode);
        return cityWithoutPostcode;
      } else {
        // No postcode found, return as is
        console.log('No postcode found, returning:', lastPart);
        return lastPart.trim();
      }
    }
    
    if (parts.length === 1) {
      // Single part: might be "City Postcode" or just "City"
      const singlePart = parts[0];
      console.log('1 part only:', singlePart);
      
      if (postcodePattern.test(singlePart)) {
        // Remove the postcode
        const cityWithoutPostcode = singlePart.replace(postcodePattern, '').trim();
        console.log('Single part with postcode, city is:', cityWithoutPostcode);
        return cityWithoutPostcode;
      } else {
        // No postcode, return as is
        console.log('Single part without postcode:', singlePart);
        return singlePart.trim();
      }
    }
    
    console.log('Could not extract city from:', address);
    return null;
  };

  useEffect(() => {
    fetchRides();
  }, [selectedDate, selectedService, selectedCity]);

  useEffect(() => {
    // Extract unique cities from rides
    extractCities();
  }, [rides]);

  const extractCities = () => {
    if (rides.length > 0) {
      const cityNames = rides
        .map(ride => extractCityName(ride.pickupLocation))
        .filter(city => city !== null);
      
      const uniqueCities = [...new Set(cityNames)].sort();
      setCities(uniqueCities);
    }
  };

  const fetchRides = async () => {
    try {
      let url = API_URLS.rides.available;
      const params = new URLSearchParams();
      if (selectedDate) params.append('date', selectedDate);
      if (selectedService) params.append('serviceSession', selectedService);
      if (selectedCity) params.append('city', selectedCity);
      
      const response = await axiosInstance.get(`${url}?${params.toString()}`);
      setRides(response.data);
    } catch (error) {
      console.error('Error fetching rides:', error);
      setError('Failed to fetch available rides');
    } finally {
      setLoading(false);
    }
  };

  // Backend handles all filtering, no need for client-side filtering
  const filteredRides = rides;

  const handleRequestRide = (ride) => {
    setBookingRide(ride);
    setSelectedSeats(1);
    setSpecialRequests('');
  };

  const handleCancelBooking = () => {
    setBookingRide(null);
    setBookingStatus({ loading: false, error: '', success: false });
    setSpecialRequests('');
  };

  const handleConfirmBooking = async () => {
    if (!bookingRide) return;
    
    setBookingStatus({ loading: true, error: '', success: false });
    
    try {
      // Create a ride request instead of direct booking
      const response = await axiosInstance.post('/api/bookings', {
        pickupLocation: bookingRide.pickupLocation,
        destination: 'Unit 2, 3740 27 St NE, Calgary, Alberta T1Y 5E2',
        date: bookingRide.date,
        serviceSession: bookingRide.serviceSession,
        serviceTime: bookingRide.time,
        passengers: selectedSeats,
        seats: selectedSeats,
        rideId: bookingRide.id, // Link to the specific ride
        specialRequests: specialRequests.trim() || undefined
      });
      
      console.log('Ride request successful:', response.data);
      setBookingStatus({ loading: false, error: '', success: true });
      
      setTimeout(() => {
        setBookingRide(null);
        fetchRides();
      }, 2000);
    } catch (error) {
      console.error('Error requesting ride:', error);
      setBookingStatus({ 
        loading: false, 
        error: error.response?.data?.message || 'Failed to request ride', 
        success: false 
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Available Rides</h1>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="date" className="block text-sm font-medium text-gray-700">
            Date
          </label>
          <input
            type="date"
            id="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label htmlFor="service" className="block text-sm font-medium text-gray-700">
            Service
          </label>
          <select
            id="service"
            value={selectedService}
            onChange={(e) => setSelectedService(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">All Services</option>
            <option value="1st">1st Service</option>
            <option value="2nd">2nd Service</option>
            <option value="3rd">3rd Service</option>
            <option value="midweek">Midweek</option>
          </select>
        </div>
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700">
            City
          </label>
          <select
            id="city"
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">All Cities</option>
            {cities.map((city, index) => (
              <option key={index} value={city}>{city}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4">Loading...</div>
      ) : filteredRides.length === 0 ? (
        <div className="text-center py-4 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No available rides found</p>
          <button
            onClick={() => navigate('/rider/book-ride')}
            className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Request a Ride
          </button>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredRides.map((ride) => (
            <div key={ride.id} className="bg-white shadow rounded-lg overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-5 w-5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">
                      {formatDate(ride.date)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* Ride Type Indicator */}
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                      ride.rideType === 'return' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {ride.rideType === 'return' ? (
                        <>
                          <ArrowUturnLeftIcon className="h-3 w-3 mr-1" />
                          Return
                        </>
                      ) : (
                        <>
                          <ArrowRightIcon className="h-3 w-3 mr-1" />
                          One-way
                        </>
                      )}
                    </div>
                    {/* Service Badge */}
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {ride.serviceSession ? `${ride.serviceSession} Service` : 'Service TBD'}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-500">
                    <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                    {ride.time || 'Time TBD'}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                    {ride.availableSeats} seats available
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mr-2" />
                    {getCityAndPostcode(ride.pickupLocation)}
                  </div>
                  {ride.vehicle && (
                    <div className="text-sm text-gray-500">
                      Vehicle: {ride.vehicle.make} {ride.vehicle.model}
                    </div>
                  )}
                </div>

                <div className="mt-6">
                  <button
                    onClick={() => handleRequestRide(ride)}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Request This Ride
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {bookingRide && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Request This Ride</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-2">
                <span className="font-medium">Date:</span> {formatDate(bookingRide.date)}
              </p>
              <p className="text-sm text-gray-500 mb-2">
                <span className="font-medium">Service:</span> {bookingRide.serviceSession} Service
              </p>
              <p className="text-sm text-gray-500 mb-2">
                <span className="font-medium">Ride Type:</span> {bookingRide.rideType === 'return' ? 'Return Trip' : 'One-way'}
              </p>
              <p className="text-sm text-gray-500 mb-2">
                <span className="font-medium">Location:</span> {getCityAndPostcode(bookingRide.pickupLocation)}
              </p>
              <p className="text-sm text-gray-500 mb-2">
                <span className="font-medium">Available Seats:</span> {bookingRide.availableSeats}
              </p>
              {bookingRide.vehicle && (
                <p className="text-sm text-gray-500 mb-2">
                  <span className="font-medium">Vehicle:</span> {bookingRide.vehicle.make} {bookingRide.vehicle.model}
                </p>
              )}
            </div>
            
            <div className="mb-6">
              <label htmlFor="seats" className="block text-sm font-medium text-gray-700 mb-1">
                How many seats do you need?
              </label>
              <select
                id="seats"
                value={selectedSeats}
                onChange={(e) => setSelectedSeats(parseInt(e.target.value))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                disabled={bookingStatus.loading || bookingStatus.success}
              >
                {[...Array(Math.min(bookingRide.availableSeats, 4)).keys()].map(i => (
                  <option key={i+1} value={i+1}>{i+1} {i === 0 ? 'seat' : 'seats'}</option>
                ))}
              </select>
            </div>

            {/* Special Requests */}
            <div className="mb-6">
              <label htmlFor="specialRequests" className="block text-sm font-medium text-gray-700 mb-1">
                Special Requests (Optional)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 pt-3 flex items-start pointer-events-none">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  id="specialRequests"
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  rows="3"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Any special requests or notes for the driver? (e.g., wheelchair accessible, child seat needed, etc.)"
                  disabled={bookingStatus.loading || bookingStatus.success}
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Let the driver know about any special requirements or preferences you may have.
              </p>
            </div>
            
            {bookingStatus.error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {bookingStatus.error}
              </div>
            )}
            
            {bookingStatus.success && (
              <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg text-sm">
                Request sent successfully! The driver will review your request and you'll be notified once they respond.
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelBooking}
                className="inline-flex justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                disabled={bookingStatus.loading || bookingStatus.success}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmBooking}
                className="inline-flex justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                disabled={bookingStatus.loading || bookingStatus.success}
              >
                {bookingStatus.loading ? 'Sending Request...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailableRides; 